import { ChevronLeft, Plus, X } from "lucide-react";
import { CATEGORIES, IMAGES, slugify, emptySection } from "./blogCmsUtils";

const saveLabel = (busy, isNew) => {
  if (busy) return "Saving…";
  return isNew ? "Publish article" : "Save changes";
};

export const BlogEditor = ({ editing, setEditing, isNew, busy, onSave, onBack }) => {
  const setSec = (i, patch) => setEditing({ ...editing, sections: editing.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  return (
    <div data-testid="blog-editor">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-xs uppercase tracking-wide font-semibold text-[#009900] hover:underline mb-5" data-testid="blog-editor-back">
        <ChevronLeft className="w-4 h-4" /> All articles
      </button>
      <div className="bg-[#FFDC00]/20 border border-[#FFDC00] rounded-lg px-4 py-3 text-xs text-[#0F1E17] mb-5" data-testid="brand-voice-note">
        <strong>Europcar voice:</strong> Elegant · Inspiring · Empathetic. Write to the customer ("you"), stay transparent on pricing, and link readers to our rental products — a "Book now" CTA is added to every article automatically. Competitor and third-party rental brand mentions are blocked on save.
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="field-label">Title *</label>
            <input className="field-input" value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: isNew ? slugify(e.target.value) : editing.slug })}
              data-testid="blog-title-input" />
          </div>
          <div>
            <label className="field-label">Slug {isNew ? "" : "(locked)"}</label>
            <input className="field-input font-mono text-xs" value={editing.slug} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} data-testid="blog-slug-input" />
          </div>
          <div>
            <label className="field-label">Category</label>
            <select className="field-input" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="blog-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Excerpt *</label>
            <textarea rows={2} className="field-input" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} data-testid="blog-excerpt-input" />
          </div>
          <div>
            <label className="field-label">Reading time (min)</label>
            <input type="number" min={1} max={30} className="field-input" value={editing.reading_time} onChange={(e) => setEditing({ ...editing, reading_time: e.target.value })} data-testid="blog-readtime-input" />
          </div>
          <div>
            <label className="field-label">Status</label>
            <select className="field-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="blog-status-select">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        <div>
          <label className="field-label">Hero image</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {IMAGES.map((url) => (
              <button key={url} type="button" onClick={() => setEditing({ ...editing, image: url })}
                className={`w-20 h-14 rounded-md overflow-hidden border-2 ${editing.image === url ? "border-[#009900]" : "border-transparent opacity-70 hover:opacity-100"}`}
                data-testid="blog-image-option">
                <img src={url} alt="option" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <input className="field-input font-mono text-xs" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="blog-image-input" />
        </div>

        <div className="space-y-4">
          <label className="field-label">Sections</label>
          {editing.sections.map((s, i) => (
            <div key={s._key} className="border border-[#E2E8F0] rounded-xl p-4 space-y-3 relative" data-testid={`blog-section-${i}`}>
              {editing.sections.length > 1 && (
                <button type="button" onClick={() => setEditing({ ...editing, sections: editing.sections.filter((x) => x._key !== s._key) })}
                  className="absolute top-3 right-3 text-red-500 hover:text-red-700" data-testid={`blog-section-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
              <input className="field-input" placeholder="Section heading" value={s.heading} onChange={(e) => setSec(i, { heading: e.target.value })} data-testid={`blog-section-heading-${i}`} />
              <textarea rows={4} className="field-input" placeholder="Paragraphs — separate with a blank line" value={s.paragraphsText} onChange={(e) => setSec(i, { paragraphsText: e.target.value })} data-testid={`blog-section-paragraphs-${i}`} />
              <textarea rows={2} className="field-input" placeholder="Bullet points (optional) — one per line" value={s.bulletsText} onChange={(e) => setSec(i, { bulletsText: e.target.value })} data-testid={`blog-section-bullets-${i}`} />
            </div>
          ))}
          <button type="button" onClick={() => setEditing({ ...editing, sections: [...editing.sections, emptySection()] })}
            className="border-2 border-[#009900] text-[#009900] rounded px-4 py-2 text-xs font-medium uppercase tracking-wide hover:bg-[#f0f9f0] flex items-center gap-1.5" data-testid="blog-add-section-btn">
            <Plus className="w-4 h-4" /> Add section
          </button>
        </div>

        <div>
          <label className="field-label">Key takeaways — one per line</label>
          <textarea rows={3} className="field-input" value={editing.takeawaysText} onChange={(e) => setEditing({ ...editing, takeawaysText: e.target.value })} data-testid="blog-takeaways-input" />
        </div>

        <button onClick={onSave} disabled={busy} className="btn-cta rounded w-full py-3.5 text-sm" data-testid="blog-save-btn">
          {saveLabel(busy, isNew)}
        </button>
      </div>
    </div>
  );
};
